import React, { useCallback, useEffect, useState } from 'react';
import {
  IconArrowRight,
  IconBan,
  IconCalendarEvent,
  IconMessage,
  IconPencil,
  IconPlus,
} from '@tabler/icons-react';
import type { BoardStage } from '../../../../entities/board';
import type { RuleRecord } from '../../../../entities/rule';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { RuleModal } from '../../../../features/admin-rule-form';
import { DATE_LEAD_FIELDS, LEAD_FIELDS, SMS_EVENT_OPTIONS } from '../../../../features/admin-rule-form/model/types';

const pluralRules = (n: number) => {
  if (n === 1) return '1 правило';
  if (n < 5) return `${n} правила`;
  return `${n} правил`;
};

const fieldLabel = (v: string) => LEAD_FIELDS.find(f => f.value === v)?.label ?? v;
const dateLabel  = (v: string) => DATE_LEAD_FIELDS.find(f => f.value === v)?.label ?? v;
const eventLabel = (v: string) => SMS_EVENT_OPTIONS.find(e => e.value === v)?.label ?? v;

const RulesSection: React.FC = () => {
  const [rules, setRules] = useState<RuleRecord[]>([]);
  const [stages, setStages] = useState<BoardStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; rule?: RuleRecord } | null>(null);

  const loadRules = useCallback(() => {
    setLoading(true);
    pb.collection('rulesBoard')
      .getFullList<RuleRecord>({
        sort: '-created',
        expand: 'triggerColumn,targetColumn,smsTemplateId,smsProviderId',
      })
      .then(setRules)
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    pb.collection('board')
      .getFullList<BoardStage>({ sort: 'order', fields: 'id,name,order,actual,colors' })
      .then(setStages)
      .catch(() => setStages([]));
    loadRules();
  }, [loadRules]);

  const stageChip = (stage: BoardStage | undefined, fallbackId: string) => {
    const name = stage?.name ?? stages.find(s => s.id === fallbackId)?.name ?? fallbackId;
    const c = stage?.colors;
    return (
      <span
        className="adm-rule-stage-chip"
        style={c ? { background: c.headerBg, color: c.nameColor, borderColor: c.dotBg + '55' } : undefined}
      >
        {c && <span className="adm-rule-stage-dot" style={{ background: c.dotBg }} />}
        {name || '—'}
      </span>
    );
  };

  const chip = (content: React.ReactNode, variant?: 'date' | 'sms' | 'muted') => (
    <span className={`adm-rule-field-chip${variant ? ` adm-rule-${variant}-chip` : ''}`}>
      {content}
    </span>
  );

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Правила</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : pluralRules(rules.length)}</p>
        </div>
        <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
          <IconPlus size={15} stroke={2.5} />Создать правило
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-rules-list">
          {rules.map(r => {
            const isRestrict = r.ruleType === 'RESTRICT';
            const isDateMove = r.ruleType === 'DATE_MOVE';
            const isSms     = r.ruleType === 'SMS';

            const triggerStage = r.expand?.triggerColumn;
            const targetStage  = r.expand?.targetColumn;
            const fields = Array.isArray(r.requiredFields) ? r.requiredFields : [];
            const smsData = r.data as Record<string, string> | null;
            const smsName = smsData?.name;

            return (
              <div key={r.id} className={`adm-rule-card${r.actual ? '' : ' inactive'}`}>

                {/* ── Header row ─────────────────────────────── */}
                <div className="adm-rule-head">
                  <div className="adm-rule-left" style={{ flexWrap: 'wrap', gap: 6 }}>

                    {/* Type badge */}
                    <span className={`adm-rule-type-badge ${isRestrict ? 'restrict' : isDateMove ? 'datemove' : 'sms'}`}>
                      {isRestrict  && <IconBan size={11} stroke={2} />}
                      {isDateMove  && <IconCalendarEvent size={11} stroke={2} />}
                      {isSms       && <IconMessage size={11} stroke={2} />}
                      {isRestrict ? 'Ограничение' : isDateMove ? 'Перенос по дате' : 'SMS уведомление'}
                    </span>

                    {/* RESTRICT: trigger column */}
                    {isRestrict && r.triggerColumn && stageChip(triggerStage, r.triggerColumn)}

                    {/* DATE_MOVE: date field → target column */}
                    {isDateMove && (
                      <>
                        {chip(dateLabel(r.dateField ?? ''), 'date')}
                        {r.targetColumn && (
                          <>
                            <IconArrowRight size={13} stroke={1.5} className="adm-rule-arrow" />
                            {stageChip(targetStage, r.targetColumn)}
                          </>
                        )}
                      </>
                    )}

                    {/* SMS: name + event */}
                    {isSms && (
                      <>
                        {smsName && (
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text, #111827)' }}>
                            {smsName}
                          </span>
                        )}
                        {smsData?.event && chip(eventLabel(smsData.event), 'date')}
                        {smsData?.event === 'STAGE_CHANGE' && r.triggerColumn &&
                          stageChip(triggerStage, r.triggerColumn)}
                      </>
                    )}
                  </div>

                  <div className="adm-rule-right">
                    <span
                      className={`adm-rule-status-dot${r.actual ? ' active' : ''}`}
                      title={r.actual ? 'Активно' : 'Отключено'}
                    />
                    <Button
                      variant="adm-edit"
                      type="button"
                      title="Редактировать"
                      onClick={() => setModal({ mode: 'edit', rule: r })}
                    >
                      <IconPencil size={14} stroke={2} />
                    </Button>
                  </div>
                </div>

                {/* ── SMS detail row ──────────────────────────── */}
                {isSms && (
                  <div className="adm-rule-fields-row">
                    {r.expand?.smsTemplateId && chip(r.expand.smsTemplateId.name, 'sms')}
                    {r.expand?.smsProviderId && chip(r.expand.smsProviderId.name, 'muted')}
                    {r.smsRecipient && chip(r.smsRecipient === 'candidate' ? 'Кандидат' : 'Исполнитель')}
                    {r.runOnce && chip('1×', 'muted')}
                    {Array.isArray(r.groupIds) && r.groupIds.length > 0 && chip(`${r.groupIds.length} гр.`, 'muted')}
                  </div>
                )}

                {/* ── Required fields row ─────────────────────── */}
                {fields.length > 0 && (
                  <div className="adm-rule-fields-row">
                    {fields.map(f => (
                      <span key={f} className="adm-rule-field-chip">{fieldLabel(f)}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!loading && rules.length === 0 && <div className="adm-empty">Правила не созданы</div>}
        </div>
      )}

      {modal && (
        <RuleModal
          mode={modal.mode}
          initial={modal.rule ?? null}
          stages={stages}
          onClose={() => setModal(null)}
          onSaved={loadRules}
        />
      )}
    </div>
  );
};

export { RulesSection };
